// Ludzie — wspólne słowa i rachunki modułu: rodzaje ważnych dat i kontaktów, „Jak często",
// „za 3 dni · urodziny", wiek albo lata, „3 tyg. temu". Używają ich strona /ludzie, Planer
// (wpis całodniowy) i „Teraz" asystenta. Teksty z people.* czytane leniwie (język zmienia się w trakcie).
import { t } from '@/i18n/index.js';
import { intlLocale } from '@/composables/useLanguage.js';

export const DATE_KINDS = ['birthday', 'nameday', 'anniversary', 'custom'];
export const CONTACT_KINDS = ['call', 'meeting', 'message', 'other'];
export const CONTACT_ICONS = { call: 'pi pi-phone', meeting: 'pi pi-users', message: 'pi pi-comment', other: 'pi pi-circle' };

// „Jak często" do wyboru (dni); null = brak przypominania.
export const CONTACT_EVERY = [
    { value: null, key: 'none' },
    { value: 7, key: 'week' },
    { value: 14, key: 'twoWeeks' },
    { value: 30, key: 'month' },
    { value: 90, key: 'threeMonths' },
];

export const everyLabel = (days) => {
    const known = CONTACT_EVERY.find(o => o.value === (days ?? null));
    return known ? t(`people.every.${known.key}`) : t('people.every.days', { n: days });
};

// Ikona ważnej daty: prezent przy urodzinach, serce przy reszcie.
export const dateIcon = (kind) => (kind === 'birthday' ? 'pi pi-gift' : 'pi pi-heart');

// Nazwa daty na plakietce: „urodziny", „imieniny", „rocznica" albo własna nazwa.
export const dateWhat = (date) => (date.kind === 'custom' ? (date.label ?? '') : t(`people.kindsShort.${date.kind}`));

// „dziś", „jutro", „za 5 dni".
export const inDaysLabel = (n) => {
    if (n <= 0) return t('people.when.today');
    if (n === 1) return t('people.when.tomorrow');
    return t('people.when.inDays', { n }, n);
};

// Plakietka przy osobie: „za 3 dni · urodziny".
export const nextDateBadge = (next) => (next ? t('people.badge', { when: inDaysLabel(next.in_days), what: dateWhat(next) }) : null);

// „skończy 30" przy urodzinach, „5. rocznica" przy reszcie; null bez roku.
export const ageLabel = (date, inDays = date.in_days) => {
    const n = date.age_or_years;
    if (n == null) return null;
    if (date.kind === 'birthday') return t(inDays === 0 ? 'people.age.birthdayToday' : 'people.age.birthday', { n });
    return t('people.age.years', { n });
};

// Tytuł wpisu w Planerze: „🎂 Urodziny: Kasia (30)".
export const plannerTitle = (pd) => {
    const name = pd.person?.nickname ? `${pd.person.name} (${pd.person.nickname})` : pd.person?.name ?? '';
    const title = t(`people.planner.${pd.kind}`, { name, label: pd.label ?? '' });
    return pd.age_or_years != null && pd.kind !== 'nameday' ? t('people.planner.withAge', { title, n: pd.age_or_years }) : title;
};

// Dni od dnia (YYYY-MM-DD) do dziś, w strefie urządzenia.
export const daysSince = (iso, now = new Date()) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
    const then = new Date(y, m - 1, d);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((today - then) / 86400000);
};

// „wczoraj", „5 dni temu", „3 tydz. temu", „2 mies. temu".
export const agoLabel = (iso) => {
    const days = daysSince(iso);
    if (days == null) return null;
    if (days <= 0) return t('people.ago.today');
    if (days === 1) return t('people.ago.yesterday');
    if (days < 14) return t('people.ago.days', { n: days }, days);
    if (days < 60) return t('people.ago.weeks', { n: Math.round(days / 7) });
    if (days < 365) return t('people.ago.months', { n: Math.round(days / 30) });
    const years = Math.floor(days / 365);
    return t('people.ago.years', { n: years }, years);
};

// Nazwy miesięcy w języku konta (do wyboru daty).
export const monthNames = () => Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleDateString(intlLocale(), { month: 'long' }));

// Dzień i miesiąc po ludzku: „14 marca".
export const dayMonthLabel = (month, day) => new Date(2000, month - 1, day).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long' });

// Ile dni ma miesiąc w roku przestępnym — 29.02 to ważna data, która wraca (28.02 w innych latach).
export const dayCountOfMonth = (month) => new Date(2000, month, 0).getDate();

// Kolejność listy: najpierw najbliższa ważna data, potem alfabetycznie.
export const comparePeople = (a, b) => {
    const da = a.next_date?.in_days ?? Infinity;
    const db = b.next_date?.in_days ?? Infinity;
    if (da !== db) return da - db;
    return a.name.localeCompare(b.name, intlLocale());
};
